/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useRequireAccess` for the Caintra Connect frontend.
 * @module hooks/useRequireAccess
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router";
import {
  AUTH_SESSION_STATUS,
  useAuthSession,
} from "../context/AuthSessionContext";

/**
 * Requires an authenticated session and that `canAccess(user, permissions)` is true.
 * Reads from the shared auth session synchronously when ready.
 * Redirects to `/` without a token, or `/home` when the role is not allowed.
 *
 * @param {(user: object|null, permissions: unknown) => boolean} canAccess
 * @returns {{ allowed: boolean, user: object|null, isSessionLoading: boolean }}
 */
const useRequireAccess = (canAccess) => {
  const navigate = useNavigate();
  const { user, permissions, status } = useAuthSession();

  const isSessionLoading =
    status === AUTH_SESSION_STATUS.IDLE ||
    status === AUTH_SESSION_STATUS.LOADING;

  const allowed =
    status === AUTH_SESSION_STATUS.READY && canAccess(user, permissions);

  useEffect(() => {
    if (status === AUTH_SESSION_STATUS.UNAUTHENTICATED) {
      navigate("/", { replace: true });
    }
  }, [navigate, status]);

  useEffect(() => {
    if (status === AUTH_SESSION_STATUS.READY && !canAccess(user, permissions)) {
      navigate("/home", { replace: true });
    }
  }, [canAccess, navigate, permissions, status, user]);

  return { allowed, user, isSessionLoading };
};

export default useRequireAccess;
